import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Button,
  Circle,
  Image
} from '@chakra-ui/react';
import { FaCoins, FaGift } from 'react-icons/fa';
import { MEGA_MILLIONS_CONFIG } from '../Games/ScratchCardConfigs';

/**
 * MegaMillionsCard Component
 * 
 * A dedicated component for rendering the MEGA MILLIONS scratch card
 * with its specific design and layout.
 */
const MegaMillionsCard = ({ 
  isPreview = false, 
  onPlay,
  onBuy,
  gameState = null 
}) => {
  const [cells, setCells] = useState(
    Array(20).fill().map((_, i) => ({ 
      id: i, 
      revealed: isPreview, 
      value: null,
      isWinning: false
    }))
  );
  
  // Handle cell click (scratch)
  const handleCellClick = (index) => {
    if (isPreview || !gameState || gameState.status !== 'active') return;
    
    // Don't allow scratching until winning symbols are revealed
    if (!gameState.winningSymbolsRevealed) {
      return;
    }
    
    const newCells = [...cells];
    newCells[index].revealed = true;
    
    // In a real game, this would come from the backend
    if (!newCells[index].value && gameState.cellValues) {
      newCells[index].value = gameState.cellValues[index];
      newCells[index].isWinning = gameState.prizeValues && 
                                 gameState.prizeValues.includes(gameState.cellValues[index]);
    }
    
    setCells(newCells);
  };
  
  // Update cells when game state changes
  useEffect(() => {
    if (gameState && gameState.revealedCells) {
      const newCells = [...cells];
      
      // Update all cells based on game state
      gameState.revealedCells.forEach(index => {
        if (index >= 0 && index < newCells.length) {
          newCells[index].revealed = true;
          if (gameState.cellValues) {
            newCells[index].value = gameState.cellValues[index];
            newCells[index].isWinning = gameState.prizeValues && 
                                      gameState.prizeValues.includes(gameState.cellValues[index]);
          }
        }
      });
      
      setCells(newCells);
    }
  }, [gameState]);
  
  // Render a cell
  const renderCell = (index) => {
    const cell = cells[index];
    const isRevealed = cell.revealed || isPreview;
    
    return (
      <Box
        key={index}
        bg="#ffc107" // Yellow background for cells to match other cards
        border="1px solid"
        borderColor="#e6a800"
        borderRadius="md"
        height="52px"
        width="52px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor={isPreview || (gameState && !gameState.winningSymbolsRevealed) ? "default" : "pointer"}
        onClick={() => handleCellClick(index)}
        position="relative"
        transition="all 0.2s"
        _hover={!isPreview && gameState && gameState.winningSymbolsRevealed && { transform: "scale(1.05)" }}
        boxShadow="0 2px 4px rgba(0,0,0,0.2)"
      >
        {isRevealed ? (
          <Box
            width="80%"
            height="80%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {gameState && gameState.cellValues && gameState.cellValues[index] ? (
              <Text fontSize="md" fontWeight="bold">{gameState.cellValues[index]}</Text>
            ) : (
              <Image src="/images/777.png" height="100%" width="100%" objectFit="contain" fallback={<Text fontSize="2xl">🎮</Text>} />
            )}
          </Box>
        ) : (
          <Box 
            position="absolute" 
            top="0" 
            left="0" 
            right="0" 
            bottom="0" 
            bg="#ffc107"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={1}
          >
            <Image src="/images/NFT/777.png" height="80%" />
          </Box>
        )}
      </Box>
    );
  };
  
  // Render the grid
  const renderGrid = () => {
    return (
      <Box
        bg="linear-gradient(145deg, #660000, #8B0000)" // Dark red gradient background for the grid container
        borderRadius="md"
        p={4}
        width="100%"
        maxW="480px"
        mt={1}
        mb={3}
        mx="auto"
        boxShadow="0 8px 16px rgba(0,0,0,0.3)"
        border="2px solid rgba(220, 20, 60, 0.5)"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Grid
          templateColumns="repeat(5, 1fr)"
          templateRows="repeat(4, 1fr)"
          gap={2}
          width="100%"
          justifyItems="center"
          alignItems="center"
        >
          {Array(20).fill().map((_, i) => (
            <GridItem key={i} display="flex" justifyContent="center" alignItems="center">
              {renderCell(i)}
            </GridItem>
          ))}
        </Grid>
      </Box>
    );
  };
  
  return (
    <Box 
      position="relative" 
      bg="linear-gradient(145deg, #660000, #8B0000)" // Dark red gradient background for MEGA MILLIONS
      p={4} 
      borderRadius="lg" 
      boxShadow="0 12px 24px rgba(0,0,0,0.4)"
      width="520px"
      height="650px"
      maxW="100%"
      mx="0"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      border="1px solid rgba(220, 20, 60, 0.3)"
    >
      {/* Cost badge */}
      <Box 
        position="absolute" 
        top="10px" 
        left="10px" 
        bg="rgba(0,0,0,0.8)" 
        color="yellow.300" 
        px={3} 
        py={1} 
        borderRadius="md"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        gap={1}
        boxShadow="0 2px 4px rgba(0,0,0,0.3)"
        border="1px solid rgba(220, 20, 60, 0.3)"
      >
        <FaCoins /> {MEGA_MILLIONS_CONFIG.cost} JINU
      </Box>
      
      {/* Creator badge if applicable */}
      <Box 
        position="absolute" 
        top="10px" 
        right="10px" 
        bg="rgba(0,0,0,0.8)" 
        color="yellow.300" 
        px={3} 
        py={1} 
        borderRadius="md"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        gap={1}
        boxShadow="0 2px 4px rgba(0,0,0,0.3)"
        border="1px solid rgba(220, 20, 60, 0.3)"
      >
        🎁 Premium Card
      </Box>
      
      {/* Card header - Title and description */}
      <VStack spacing={1} mt={16} mb={1}>
        <Heading 
          color="yellow.300" 
          fontWeight="bold" 
          fontSize="4xl"
          textAlign="center"
          textShadow="0 2px 4px rgba(0,0,0,0.5)"
        >
          {MEGA_MILLIONS_CONFIG.name}
        </Heading>
        <Text 
          color="white" 
          fontWeight="bold" 
          fontSize="lg"
          textAlign="center"
          textShadow="0 1px 2px rgba(0,0,0,0.5)"
        >
          Win up to 100,000,000 JINU!
        </Text>
      </VStack>
      
      {/* Winning Symbols Display */}
      <Box textAlign="center" mt={1} mb={2}>
        <HStack justify="center" spacing={2} wrap="nowrap">
          {Array.from({ length: 5 }).map((_, i) => (
            <Circle 
              key={i} 
              size="45px" 
              bg={gameState && gameState.winningSymbolsRevealed ? "linear-gradient(145deg, #DC143C, #8B0000)" : "linear-gradient(145deg, #660000, #8B0000)"}
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="2px solid"
              borderColor={gameState && gameState.winningSymbolsRevealed ? "rgba(255, 255, 255, 0.8)" : "rgba(255, 255, 255, 0.3)"}
              boxShadow={gameState && gameState.winningSymbolsRevealed ? "0 0 15px rgba(220, 20, 60, 0.5)" : "none"}
              transition="all 0.3s ease"
              mx={1}
            >
              {gameState && gameState.winningSymbolsRevealed && gameState.prizeValues && gameState.prizeValues[i] ? (
                <Text fontSize="sm" fontWeight="bold">{gameState.prizeValues[i]}</Text>
              ) : (
                <Text fontSize="xl">🎁</Text>
              )}
            </Circle>
          ))}
        </HStack>
        <Text 
          color="red.300" 
          fontSize="md" 
          fontWeight="bold"
          textShadow="0 1px 2px rgba(0,0,0,0.5)"
          mt={1}
        >
          Winning Symbols
        </Text>
      </Box>
      
      {/* Card grid */}
      {renderGrid()}
      
      {/* Match instructions */}
      <Text 
        color="white" 
        fontSize="sm"
        textAlign="center" 
        mt={2}
        mb={3}
        fontWeight="medium"
        textShadow="0 1px 2px rgba(0,0,0,0.5)"
      >
        Match any of your numbers to the winning symbols to win that prize!
      </Text>
    </Box>
  );
};

export default MegaMillionsCard;
